import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import API from '../backend/api';
import './ManualApplication.css';

const ManualApplication = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company: '',
    position: '',
    status: 'submitted',
    notes: '',
    appliedDate: new Date().toISOString().split('T')[0],
    jobUrl: '',
    salary: '',
    location: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const statusOptions = [
    { value: 'submitted', label: 'Submitted' },
    { value: 'interview', label: 'Interview' },
    { value: 'assessment', label: 'Assessment' },
    { value: 'offer', label: 'Offer' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setError('Please log in first');
      return;
    }

    if (!formData.company || !formData.position) {
      setError('Company and Position are required');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      const response = await API.post('/applications/manual/', {
        company: formData.company,
        position: formData.position,
        status: formData.status,
        notes: formData.notes,
        applied_date: formData.appliedDate,
        job_url: formData.jobUrl,
        salary: formData.salary,
        location: formData.location
      }, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`
        }
      });

      setMessage('Application added successfully! Redirecting to dashboard...');
      
      // Navigate to dashboard after a short delay to show the success message
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);

    } catch (err) {
      console.error('Error adding application:', err);
      setError(err.response?.data?.error || 'Failed to add application');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="manual-application-container">
        <div className="login-required">
          <h2>Manual Application Form</h2>
          <p>Please log in to add applications manually.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manual-application-container">
      <div className="form-header">
        <h2>Add Job Application Manually</h2>
        <p>Fill out the form below to add a job application to your tracker.</p>
      </div>

      <form onSubmit={handleSubmit} className="application-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="company">Company *</label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              required
              placeholder="e.g., Google, Microsoft, Amazon"
            />
          </div>

          <div className="form-group">
            <label htmlFor="position">Position *</label>
            <input
              type="text"
              id="position"
              name="position"
              value={formData.position}
              onChange={handleChange}
              required
              placeholder="e.g., Software Engineer, Product Manager"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="appliedDate">Applied Date</label>
            <input
              type="date"
              id="appliedDate"
              name="appliedDate"
              value={formData.appliedDate}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="location">Location</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., San Francisco, CA (Remote)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="salary">Salary</label>
            <input
              type="text"
              id="salary"
              name="salary"
              value={formData.salary}
              onChange={handleChange}
              placeholder="e.g., $120,000 - $150,000"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="jobUrl">Job URL</label>
          <input
            type="url"
            id="jobUrl"
            name="jobUrl"
            value={formData.jobUrl}
            onChange={handleChange}
            placeholder="https://company.com/careers/job-posting"
          />
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="4"
            placeholder="Any additional notes about this application..."
          />
        </div>

        <div className="form-actions">
          <button
            type="submit"
            disabled={isSubmitting}
            className="submit-button"
          >
            {isSubmitting ? 'Adding Application...' : 'Add Application'}
          </button>
        </div>

        {message && (
          <div className="success-message">
            {message}
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
      </form>
    </div>
  );
};

export default ManualApplication;
